import { createFileRoute, Link } from "@tanstack/react-router";
import { PHUKET_SEGMENTS } from "@/lib/phuket-segments";
import { ProvincePageShell } from "@/components/ProvincePageShell";
import { ChevronRight } from "lucide-react";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import { getActiveProvince } from "@/provinces/_registry";
import { LongFormContent } from "@/components/LongFormContent";
import type { PageContent } from "@/content/site-pages";

export const Route = createFileRoute("/for/")({
  loader: async (): Promise<{ hub: PageContent }> => {
    const { HUB_FOR_CONTENT } = await import("@/content/longform-hubs");
    return { hub: HUB_FOR_CONTENT };
  },
  head: ({ loaderData }) => {
    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: `บริการเฉพาะกลุ่ม — Expat, Nomad, Yacht | ${config.nameEn}`,
      description: `บริการ Notary, แปลและวีซ่าสำหรับ Expat, Digital Nomad, ลูกเรือยอตช์, นักลงทุนวิลล่า, คู่รักไทย–ต่างชาติ, ผู้เกษียณ และนักเรียนต่างชาติใน${config.nameTh}`,
      path: "/for",
    });
    return {
      ...base,
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: "/" },
              { name: "บริการเฉพาะกลุ่ม", url: "/for" },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `กลุ่มลูกค้าที่เราดูแลใน${config.nameTh}`,
            numberOfItems: PHUKET_SEGMENTS.length,
            itemListElement: PHUKET_SEGMENTS.map((s, idx) => ({
              "@type": "ListItem",
              position: idx + 1,
              name: s.titleTh,
              url: `${site}/for/${s.slug}`,
              description: s.shortTh,
            })),
          }),
        },
        ...(loaderData?.hub
          ? [
              {
                type: "application/ld+json",
                children: JSON.stringify(buildFaqSchema(loaderData.hub.faqs)),
              },
            ]
          : []),
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/for")) },
      ],
    };
  },
  component: SegmentsIndex,
});

function SegmentsIndex() {
  const { config } = getActiveProvince();
  const { hub } = Route.useLoaderData();
  return (
    <ProvincePageShell
      eyebrow="Tailored Services"
      title={`บริการเฉพาะกลุ่มลูกค้าใน${config.nameTh}`}
      subtitle={`เลือกกลุ่มของคุณเพื่อดูบริการ Notary, วีซ่า และเอกสารที่ตรงกับความต้องการมากที่สุด — ออกแบบมาสำหรับชีวิตจริงใน${config.nameTh}`}
      breadcrumb={[{ label: "Home", to: "/" }, { label: "บริการเฉพาะกลุ่ม" }]}
    >
      <div className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {PHUKET_SEGMENTS.map((s) => (
            <Link
              key={s.slug}
              to="/for/$segment"
              params={{ segment: s.slug }}
              className="group rounded-2xl border border-border bg-card p-6 shadow-[var(--shadow-card)] transition-all hover:-translate-y-1 hover:border-gold"
            >
              <div className="text-4xl">{s.emoji}</div>
              <h3 className="mt-3 text-lg font-extrabold text-primary group-hover:text-gold">{s.titleTh}</h3>
              <div className="mt-1 text-[10px] font-bold uppercase tracking-widest text-gold">{s.titleEn}</div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground">{s.shortTh}</p>
              <div className="mt-4 inline-flex items-center gap-1 text-[11px] font-bold text-gold">
                ดูบริการสำหรับกลุ่มนี้ <ChevronRight className="h-3 w-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>
      <LongFormContent content={hub} />
    </ProvincePageShell>
  );
}